/**
 * UI Configuration for Cell Swap Game
 * 
 * Centralized configuration for UI styling, fonts, colors, and layout constants.
 * This ensures consistency across all scenes and components.
 */

/**
 * Font Configuration
 */
export const FontConfig = {
    /** Primary font family with fallback */
    primary: '"M PLUS Rounded 1c", sans-serif',

    /** Alternative font (currently same as primary) */
    secondary: '"M PLUS Rounded 1c", sans-serif',

    /** Monospace font for technical displays */
    monospace: '"Courier New", monospace',
} as const

/**
 * Common text styles for consistent styling across the app
 */
export const TextStyles = {
    /** Large title text */
    title: {
        fontFamily: FontConfig.primary,
        fontSize: '48px',
        fontStyle: 'bold',
        color: '#2c3e50',
    },

    /** Medium title text */
    heading: {
        fontFamily: FontConfig.primary,
        fontSize: '32px',
        fontStyle: 'bold',
        color: '#2c3e50',
    },

    /** Section header text */
    subheading: {
        fontFamily: FontConfig.primary,
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#34495e',
    },

    /** Regular body text */
    body: {
        fontFamily: FontConfig.primary,
        fontSize: '16px',
        color: '#2c3e50',
    },

    /** Small body text */
    bodySmall: {
        fontFamily: FontConfig.primary,
        fontSize: '14px',
        color: '#2c3e50',
    },

    /** Button text */
    button: {
        fontFamily: FontConfig.primary,
        fontSize: '18px',
        color: '#ffffff',
    },

    /** Small button text */
    buttonSmall: {
        fontFamily: FontConfig.primary,
        fontSize: '14px',
        color: '#ffffff',
    },

    /** Label text */
    label: {
        fontFamily: FontConfig.primary,
        fontSize: '14px',
        color: '#7f8c8d',
    },

    /** HUD stats text with stroke */
    hudStat: {
        fontFamily: FontConfig.primary,
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 1,
    },

    /** HUD label text */
    hudLabel: {
        fontFamily: FontConfig.primary,
        fontSize: '14px',
        color: '#ecf0f1',
        stroke: '#2c3e50',
        strokeThickness: 1,
    },

    /** Floating text effect */
    floatingText: {
        fontFamily: FontConfig.primary,
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
    },
} as const

/**
 * Color Palette
 */
export const Colors = {
    // Primary colors
    primary: '#8b5cf6',
    primaryDark: '#7c3aed',
    primaryLight: '#a78bfa',

    // Status colors
    success: '#27ae60',
    warning: '#f39c12',
    danger: '#e74c3c',
    info: '#3498db',

    // Puyo colors
    red: '#FF4444',
    blue: '#4444FF',
    green: '#44FF44',
    yellow: '#FFFF44',
    purple: '#FF44FF',

    // UI colors
    background: '#2c3e50',
    backgroundLight: '#34495e',
    text: '#2c3e50',
    textLight: '#7f8c8d',
    textInverse: '#ffffff',
    border: '#bdc3c7',

    // Button colors
    buttonDefault: '#34495e',
    buttonHover: '#2c3e50',
    buttonActive: '#1a252f',
} as const

/**
 * Soft Button Color Palette (hex numbers for Phaser Graphics)
 *
 * Vibrant, playful colors for the modern soft-button system.
 * Each role has a distinct color identity to keep the UI scannable.
 */
export const SoftButtonColors = {
    /** Title menu hierarchy (plum spectrum) */
    menuContinue: 0xe89c39,   // gold – highest priority
    menuStart: 0xc8618d,      // light plum
    menuHelp: 0xa04171,       // deep plum
    menuLearn: 0xab4578,      // vibrant plum (between start and help)
    menuSettings: 0x8a3662,   // darkest plum

    /** In-game action buttons */
    pause: 0x3498db,          // clear blue
    info: 0x9b59b6,           // purple

    /** Navigation */
    back: 0x4a6fa5,           // slate blue
    reset: 0xe74c3c,          // red

    /** Confirm / play */
    play: 0x27ae60,           // green

    /** Game Center */
    gameCenter: 0x8a3662,     // plum (matching menu theme)

    /** Directional game controls */
    control: 0x546e8a,        // muted steel blue
    controlPress: 0x3d5a80,   // darker steel on press

    /** Settings tab buttons */
    tabActive: 0x3498db,      // clear blue (active/selected tab)
    tabInactive: 0x7f8c9a,    // muted slate (inactive tab)
} as const

/**
 * Layout Configuration
 */
export const LayoutConfig = {
    /** Sidebar width for cell legend */
    sidebarWidth: 200,

    /** HUD panel width */
    hudWidth: 300,

    /** Minimum compact mode width threshold */
    compactThreshold: 250,

    /** Grid configuration */
    grid: {
        rows: 12,
        columns: 6,
        cellSize: 96,
    },

    /** Spacing values */
    spacing: {
        xs: 4,
        sm: 8,
        md: 16,
        lg: 24,
        xl: 32,
    },

    /** Border radius */
    borderRadius: {
        sm: 4,
        md: 8,
        lg: 16,
    },
} as const

/**
 * Animation Configuration
 */
export const AnimationConfig = {
    /** Default animation duration (ms) */
    defaultDuration: 300,

    /** Fast animation duration (ms) */
    fastDuration: 150,

    /** Slow animation duration (ms) */
    slowDuration: 600,

    /** Default easing function */
    defaultEase: 'Power2',
} as const

/**
 * Helper function to create text style objects
 * Merges base style with custom overrides
 */
export function createTextStyle(
    baseStyle: keyof typeof TextStyles,
    overrides?: Partial<Phaser.Types.GameObjects.Text.TextStyle>
): Phaser.Types.GameObjects.Text.TextStyle {
    return {
        ...TextStyles[baseStyle],
        ...overrides,
    } as Phaser.Types.GameObjects.Text.TextStyle
}

/**
 * Helper function to create responsive text style
 * Adjusts font size based on compact mode
 */
export function createResponsiveTextStyle(
    baseStyle: keyof typeof TextStyles,
    isCompact: boolean,
    compactFontSize?: string,
    overrides?: Partial<Phaser.Types.GameObjects.Text.TextStyle>
): Phaser.Types.GameObjects.Text.TextStyle {
    const style: any = { ...TextStyles[baseStyle] }

    if (isCompact && compactFontSize) {
        style.fontSize = compactFontSize
    }

    return {
        ...style,
        ...overrides,
    } as Phaser.Types.GameObjects.Text.TextStyle
}
